import React from "react";
import { IconButton } from "@ui/buttons/icon-button";
import { NotificationsIcon } from "@ui/icons/material/Notifications";
import { Button } from "@ui/buttons/button";
import { useUserNotifications } from "./requests/user-notifications";
import { ProgressCircle } from "@ui/progress/progress-circle";
import { NotificationList } from "../notification-list";
import { DialogTrigger } from "@ui/overlays/dialog/dialog-trigger";
import { StackTrigger } from "@ui/overlays/stack-navigator/stack-trigger";
import { Dialog } from "@ui/overlays/dialog/dialog";
import { DialogHeader } from "@ui/overlays/dialog/dialog-header";
import { DialogBody } from "@ui/overlays/dialog/dialog-body";
import { Badge } from "@ui/badge/badge";
import { DoneAllIcon } from "@ui/icons/material/DoneAll";
import { useMarkNotificationsAsRead } from "../requests/use-mark-notifications-as-read";
import { useGetNotificationsCount } from "../requests/use-get-notifications-count";
import { NotificationEmptyStateMessage } from "../empty-state/notification-empty-state-message";
import { SettingsIcon } from "@ui/icons/material/Settings";
import { Link } from "react-router-dom";
import { useSettings } from "@ui/settings/use-settings";
import { useAuth } from "@common/auth/use-auth";
import { Trans } from "@ui/i18n/trans";
import { useContext } from "react";
import { DialogContext } from "@ui/overlays/dialog/dialog-context"; // Import correct context
import { isMobile, isMobileApp } from "@common/core/platform-detection";
import clsx from "clsx";

interface NotificationDialogTriggerProps<T = any> {
  className?: string;
  triggerType?: string;
}
export function NotificationDialogTrigger({
  className,
  triggerType,
}: NotificationDialogTriggerProps) {
  const { user, isLoggedIn } = useAuth();
  const { notif } = useSettings();
  const query = useUserNotifications();
  const markAsRead = useMarkNotificationsAsRead();
  const hasUnread = !!user?.unread_notifications_count;
  const isNativeMobileApp = isMobileApp();
  const isMobileDevice = isMobile();

  if (!isLoggedIn) {
    return null;
  }

  const handleMarkAsRead = () => {
    if (!query.data) return;
    markAsRead.mutate({
      markAllAsUnread: true,
    });
  };

  const triggerElement =
    !triggerType || triggerType == "icon" ? (
      <IconButton
        variant="text"
        color={"paper"}
        size={"md"}
        className={clsx("mx-8", className)}
        badge={
          hasUnread ? (
            <Badge className="">{user?.unread_notifications_count}</Badge>
          ) : undefined
        }
      >
        <NotificationsIcon />
      </IconButton>
    ) : (
      <div>
        <Trans message="Notifications" />
        {hasUnread ? ` (${user.unread_notifications_count})` : undefined}
      </div>
    );

  const overlayContent = (
    <Dialog background={isNativeMobileApp ? "bg-transparent" : undefined}>
      {!isMobileDevice && (
        <DialogHeader
          showDivider
          actions={
            !hasUnread &&
            notif.subs.integrated && (
              <IconButton
                className="text-muted"
                size="sm"
                elementType={Link}
                to="/notifications/settings"
                target="_blank"
              >
                <SettingsIcon />
              </IconButton>
            )
          }
          rightAdornment={
            hasUnread && (
              <Button
                variant="text"
                color="primary"
                size="xs"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAsRead}
                disabled={markAsRead.isPending}
                className="max-md:hidden"
              >
                <Trans message="Mark all as read" />
              </Button>
            )
          }
        >
          <Trans message="Notifications" />
        </DialogHeader>
      )}
      <DialogBody padding="p-0">
        <DialogContent />
      </DialogBody>
    </Dialog>
  );

  const stackedHeaderRight = hasUnread ? (
    <Button
      variant="text"
      color="primary"
      size="xs"
      startIcon={<DoneAllIcon />}
      onClick={handleMarkAsRead}
      disabled={markAsRead.isPending}
      className=""
    >
      <Trans message="Mark all as read" />
    </Button>
  ) : null;

  return isMobileDevice ? (
    <StackTrigger
      title="Notifications"
      stackedBodyPadding={false}
      stackedHeaderRight={stackedHeaderRight}
    >
      {triggerElement}
      {overlayContent}
    </StackTrigger>
  ) : (
    <DialogTrigger type="popover">
      {triggerElement}
      {overlayContent}
    </DialogTrigger>
  );
}

function DialogContent() {
  const { data, isLoading } = useUserNotifications();
  const { close } = useContext(DialogContext); // Use context to close dialog
  const getNotificationsCount = useGetNotificationsCount();

  const handleNotificationClick = () => {
    close(); // Close the dialog when a notification is clicked
    getNotificationsCount.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-24 py-20">
        <ProgressCircle aria-label="Loading notifications..." isIndeterminate />
      </div>
    );
  }
  if (!data?.pagination.data.length) {
    return (
      <div className="px-24 py-20">
        <NotificationEmptyStateMessage />
      </div>
    );
  }
  return (
    <div>
      <NotificationList
        notifications={data.pagination.data}
        onNotificationClick={handleNotificationClick}
      />
    </div>
  );
}
